import axios from 'axios'
import type { AxiosInstance, AxiosResponse } from 'axios'
import Cookies from 'js-cookie'
import { ref } from 'vue'
import type { ApiReturnStructure } from '../types/api'
import useFirebase from '../composables/useFirebase'
import { OrderManualOperationStatus } from '../pages/orders/types'
import type { VnOrder } from '../pages/orders/types'
import { Therapist } from '../pages/therapists/types'
import { OpsUser, UpdateOpsUserRequest } from '../pages/ops-users/types'
import router from '../router'

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL

if (!apiBaseUrl) {
    throw new Error('[API] Missing required env: VITE_API_BASE_URL')
}

const token = ref(Cookies.get('token'))
const { getAuth } = useFirebase()
const firebaseAuth = getAuth()
const axiosInstance: AxiosInstance = axios.create({
    baseURL: apiBaseUrl,
    timeout: 5000, // Timeout for requests in milliseconds
    headers: {
        'Content-Type': 'application/json',
        'authorization-firebase': token.value ? `${token.value}` : '',
    },
})
axiosInstance.interceptors.response.use(
    (response) => {
        // Handle successful response
        return response
    },
    (error) => {
        if (error.response && error.response.status === 403) {
            router.push('/auth/login') // Redirect to login if 403 error occurs
        }
        return Promise.reject(error)
    },
)
const setAuthToken = (authToken: string): void => {
    token.value = authToken
    axiosInstance.defaults.headers['authorization-firebase'] = `${authToken}`
    Cookies.set('token', authToken, { expires: 7 }) // Set the token in cookies with an expiration of 1 day
}
firebaseAuth.currentUser?.getIdToken().then((idToken) => {
    console.log('idToken', idToken)
    setAuthToken(idToken)
})
console.log('firebaseAuth', firebaseAuth)
// Create an instance of axios with custom configurations

const removeAuthToken = (): void => {
    token.value = ''
}
// Login function to authenticate user and obtain token
const login = async (): Promise<any> => {
    try {
        return axiosInstance.post('/login')
    } catch (error) {
        console.log('Error in login:', error)
        throw error // Handle error appropriately
    }
}
const updateUser = (params: User.User) => {
    return axiosInstance.patch(`user`, params)
}
const createOrder = (params: { category: string; planId: number; planParams: any }) => {
    const { category, planId, planParams } = params
    return axiosInstance.post(`plan/${category}/${planId}/order`, planParams)
}
const updateOrder = (params: { category: string; orderId: number; planParams: any }) => {
    const { category, orderId, planParams } = params
    return axiosInstance.put(`plan/${category}/order/${orderId}`, planParams)
}
const createPayment = (params: { orderId: number; paymentParams: any }) => {
    const { orderId, paymentParams } = params
    return axiosInstance.post(`plan/escort/order/${orderId}/payment`, paymentParams)
}
const getOrders = (params: {
    limit: number
    offset: number
}): Promise<AxiosResponse<ApiReturnStructure<VnOrder[]>>> => {
    return axiosInstance.get('/order/detail', { params })
}
const getOrderDetail = (orderId: number): Promise<AxiosResponse<ApiReturnStructure<Order.OrderWithPlanEscort>>> => {
    return axiosInstance.get(`/order/detail/${orderId}`)
}
const getEstimateFee = (
    planId: number,
    params: { serviceHours: string; numGuides: number },
    query: { currencyType: string },
) => {
    return axiosInstance.post(`/plan/escort/${planId}/estimateFee`, params, { params: query })
}
const updateEscortOrder = (params: { orderId: number; planParams: any }) => {
    return axiosInstance.put(`plan/escort/order/${params.orderId}`, params.planParams)
}
const assignGuidesToOrder = (params: { orderId: number; guideIds: number[] }) => {
    return axiosInstance.post(`plan/escort/order/${params.orderId}/assign-guides`, { guides: params.guideIds })
}
const updateManualOperationStatus = (params: {
    orderId: number
    manualOperationStatus: OrderManualOperationStatus
}) => {
    return axiosInstance.post(`/order/${params.orderId}/manual-operation-status`, {
        manualOperationStatus: params.manualOperationStatus,
    })
}
const updateMassageOrder = (params: {
    orderId: number
    planParams: {
        confirmedTherapistId?: number | null
        confirmedTime?: string
    }
}) => {
    return axiosInstance.patch(`plan/dragon-massage/order/${params.orderId}/confirm`, params.planParams)
}
const getTherapists = (params: {
    limit: number
    offset: number
}): Promise<AxiosResponse<ApiReturnStructure<Therapist[]>>> => {
    return axiosInstance.get('/plan/dragon-massage/therapist', { params })
}
const getOpsUsers = (): Promise<AxiosResponse<ApiReturnStructure<OpsUser[]>>> => {
    return axiosInstance.get('/ops-user')
}
const updateOpsUser = (params: UpdateOpsUserRequest) => {
    const { id, ...payload } = params
    return axiosInstance.patch(`/ops-user/${id}`, payload)
}
const addTherapist = (therapist: Therapist): Promise<AxiosResponse<Therapist>> => {
    return axiosInstance.post('/plan/dragon-massage/therapist', therapist)
}
const uploadTherapistImages = (images: File[]): Promise<AxiosResponse<{ data: { imageNames: string[] } }>> => {
    const formData = new FormData()
    images.forEach((image) => {
        formData.append('images', image)
    })
    return axiosInstance.post('/plan/dragon-massage/therapist/images/upload', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    })
}
export default {
    axiosInstance,
    login,
    removeAuthToken,
    setAuthToken,
    createOrder,
    updateOrder,
    updateUser,
    createPayment,
    getOrders,
    getOrderDetail,
    getEstimateFee,
    updateEscortOrder,
    assignGuidesToOrder,
    updateManualOperationStatus,
    updateMassageOrder,
    getTherapists,
    getOpsUsers,
    updateOpsUser,
    addTherapist,
    uploadTherapistImages,
    post: axiosInstance.post,
}
export {
    axiosInstance,
    login,
    removeAuthToken,
    setAuthToken,
    createOrder,
    updateOrder,
    updateUser,
    createPayment,
    getOrders,
    getOrderDetail,
    getEstimateFee,
    updateEscortOrder,
    assignGuidesToOrder,
    updateManualOperationStatus,
    updateMassageOrder,
    getTherapists,
    getOpsUsers,
    updateOpsUser,
    addTherapist,
    uploadTherapistImages,
}
